import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { getDynamicBulletsIndex, calculateDynamicBulletsOffset } from "./utils/dynamicBullets";
import * as styles from "./Pagination.module.scss";

const defaultLabels = {
	pagination: "Go to slide {{index}}",
};

const Pagination = ({
	className,
	classNames,
	labels: providedLabels,
	current: providedCurrent,
	total = 0,
	onClick,
}) => {
	const labels = { ...defaultLabels, ...providedLabels };
	const bulletElRefs = useRef([]);
	const [currentIndex, setCurrentIndex] = useState(providedCurrent);
	const [dynamicBulletIndex, setDynamicBulletIndex] = useState(null);
	const [firstIndex, setFirstIndex] = useState(0);
	const [lastIndex, setLastIndex] = useState(0);
	const [bulletsWidth, setBulletsWidth] = useState(0);
	const [bulletsOffset, setBulletsOffset] = useState(0);

	const updateDynamicBulletIndexes = (current, previous, index, _total) => {
		const [newFirstIndex, newLastIndex, bulletIndex] = getDynamicBulletsIndex(
			current,
			previous,
			index,
			_total
		);

		setDynamicBulletIndex(bulletIndex);
		setFirstIndex(newFirstIndex);
		setLastIndex(newLastIndex);
	};

	const updateDynamicBulletPositions = (bullets, first, last, index) => {
		const [newBulletsWidth, newBulletsOffset] = calculateDynamicBulletsOffset(
			bullets,
			first,
			last,
			index
		);

		setBulletsWidth(newBulletsWidth);
		setBulletsOffset(newBulletsOffset);
	};

	useEffect(() => {
		updateDynamicBulletIndexes(providedCurrent, currentIndex, dynamicBulletIndex, total);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (providedCurrent !== currentIndex) {
			updateDynamicBulletIndexes(providedCurrent, currentIndex, dynamicBulletIndex, total);

			// Because currentIndex is updated after bullets, providedCurrent = new index, currentIndex = previous index
			setCurrentIndex(providedCurrent);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [providedCurrent, total]);

	useEffect(() => {
		setTimeout(() => {
			updateDynamicBulletPositions(bulletElRefs.current, firstIndex, lastIndex, dynamicBulletIndex);
			// Account for 150ms animation before size of bullets are finalized
		}, 150);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentIndex, dynamicBulletIndex]);

	return (
		<div className={cx(styles.container, className)} style={{ width: bulletsWidth }}>
			<div
				className={cx(styles.pagination, classNames?.pagination)}
				style={{ transform: `translateX(${bulletsOffset}px)` }}>
				{[...Array(total).keys()].map(slideIndex => (
					<button
						key={slideIndex}
						ref={ref => {
							bulletElRefs.current[slideIndex] = ref;
						}}
						className={cx(styles.bullet, {
							[styles.active]: slideIndex === currentIndex,
							[styles.main]: slideIndex >= firstIndex && slideIndex <= lastIndex,
							[styles.edge]: slideIndex === firstIndex - 1 || slideIndex === lastIndex + 1,
						})}
						type="button"
						onClick={() => onClick(slideIndex)}
						aria-label={labels.pagination.replace("{{index}}", slideIndex + 1)}
					/>
				))}
			</div>
		</div>
	);
};

Pagination.propTypes = {
	className: PropTypes.string,
	classNames: PropTypes.shape({
		pagination: PropTypes.string,
	}),
	current: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
	onClick: PropTypes.func.isRequired,
	labels: PropTypes.shape({
		pagination: PropTypes.string,
	}),
};

Pagination.defaultProps = {
	className: undefined,
	classNames: undefined,
	labels: defaultLabels,
};

export { Pagination };
